/**
 * c-cart-gift.js
 *
 * Gift with purchase for the `c-cart-drawer` section. While the trigger
 * product is in the cart, the gift line's quantity follows the trigger's (one
 * gift per unit); when the trigger leaves, so does the gift.
 *
 * The gift is free through an automatic Buy X Get Y discount in Shopify, not
 * here. This script only manages the line, so the cart, checkout and order all
 * show the gift at its real price with the discount against it.
 *
 * Config comes from `[data-c-cart-gift]`, written by the drawer section.
 * Runs on page load and after every drawer render (`c-cart:updated`).
 */
(function () {
  'use strict';

  if (window.__cCartGiftReady) return;

  var config = null;
  try {
    config = JSON.parse(document.querySelector('[data-c-cart-gift]').textContent);
  } catch (error) {
    return;
  }
  if (!config || !config.trigger || !config.gift) return;
  window.__cCartGiftReady = true;

  var busy = false;
  var again = false;
  /* Changing syncs in a row; a cart that never settles stops here. */
  var rounds = 0;

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart error ' + response.status);
      return response.json();
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      return response.json();
    });
  }

  function giftLines(cart) {
    var lines = [];
    cart.items.forEach(function (item, index) {
      if (item.variant_id === config.gift) lines.push({ index: index + 1, key: item.key, quantity: item.quantity });
    });
    return lines;
  }

  /* Quantity changes go through the drawer's changeLine, which re-renders
     from the POST response. A GET re-render can come back with the cart as it
     was before the change. The cart page renders server side, so there the
     change is posted directly and the page reloads. */
  function setLine(line, quantity) {
    var onCartPage = /^\/cart\/?$/.test(window.location.pathname);
    if (!onCartPage && window.FM && window.FM.cart && window.FM.cart.changeLine) {
      return window.FM.cart.changeLine(line.index, quantity);
    }
    return post('/cart/change.js', { id: line.key, quantity: quantity }).then(function () {
      window.location.reload();
    });
  }

  /* Resolves true when the cart had to change. */
  function reconcile(cart) {
    var wanted = 0;
    cart.items.forEach(function (item) {
      if (item.product_id === config.trigger) wanted += item.quantity;
    });

    var lines = giftLines(cart);
    var held = lines.reduce(function (sum, line) {
      return sum + line.quantity;
    }, 0);
    if (held === wanted && lines.length <= 1) return Promise.resolve(false);

    if (!lines.length) {
      /* add.js can't return the drawer section without opening the drawer
         through FM.cart.add, so add quietly, then set the new line's
         quantity through the drawer to re-render it. */
      return post('/cart/add.js', { items: [{ id: config.gift, quantity: wanted }] })
        .then(getCart)
        .then(function (fresh) {
          var added = giftLines(fresh)[0];
          return added ? setLine(added, added.quantity) : null;
        })
        .then(function () {
          return true;
        });
    }

    /* Shopify splits the gift into two lines when only part of it is
       discounted. Clear the extra lines from the bottom up so the indexes of
       the ones above stay valid, then set the first. */
    var chain = Promise.resolve();
    lines.slice(1).reverse().forEach(function (extra) {
      chain = chain.then(function () {
        return setLine(extra, 0);
      });
    });
    return chain
      .then(function () {
        return setLine(lines[0], wanted);
      })
      .then(function () {
        return true;
      });
  }

  function sync() {
    if (busy) {
      again = true;
      return;
    }
    busy = true;

    getCart()
      .then(reconcile)
      .then(function (changed) {
        busy = false;
        rounds = changed ? rounds + 1 : 0;
        /* After a change, check once more that the cart settled. */
        if ((changed && rounds < 3) || (!changed && again)) {
          again = false;
          sync();
        } else {
          again = false;
        }
      })
      .catch(function () {
        busy = false;
        again = false;
      });
  }

  document.addEventListener('c-cart:updated', sync);
  sync();
})();
